import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Separator } from "@postcheesy/ui-lib/atoms";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@postcheesy/ui-lib/molecules";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { loginSchema } from "../../../resources/schema/login-schema";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import login from "../../../api/login";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../resources/constants/routes";
import { useState } from "react";
import resendConfirmationEmail from "../../../api/resendConfirmationEmail";
import { useUser } from "../../../resources/contexts/UserContext";
import { User } from "../../../api/manageInfo";

export function FormLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user, setUser, refreshUser } = useUser();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    defaultValues: {
      email: "",
      password: ""
    }
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    setApiError(null);
    setIsLoading(true);
    
    try {
      const result = await login(values.email, values.password);
      
      if (result.success) {
        localStorage.setItem("isAuthenticated", "true");

        setUser(prevUser => ({
          ...prevUser,
          email: prevUser?.email ?? values.email,
          isEmailConfirmed: result.emailConfirmed,
          isPhoneConfirmed: result.phoneNumberConfirmed,
          isActive: result.isActive
        }) as User);

        refreshUser();

        if (!result.emailConfirmed) {
          localStorage.setItem("email", values.email);
          resendConfirmationEmail(values.email);
          navigate(ROUTES.CONFIRM_EMAIL);
        } else if (!result.phoneNumberConfirmed) {
          navigate(ROUTES.VERIFY_PHONE);
        } else if (!result.isActive) {
          navigate(ROUTES.PROFILE_SETUP);
        } else {
          navigate(ROUTES.DASHBOARD);
        }
      } else {
        setApiError(result.message || "Une erreur s'est produite lors de la connexion.");
      }
    } catch (error: any) {
      setApiError(error.message || "Une erreur inattendue s'est produite");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form className="space-y-2" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  id="email"
                  type="email"
                  autoComplete="off"
                  placeholder="email@example.com"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mot de passe</FormLabel>
              <FormControl>
                <div className="flex items-center">
                  <Input 
                    id="password" 
                    type={showPassword ? "text" : "password"} 
                    autoComplete="off" 
                    {...field} 
                    placeholder="Mot de passe" 
                  />
                  <Button
                    type="button"
                    className="h-9"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeIcon className="w-4 h-4" /> : <EyeOffIcon className="w-4 h-4" />}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {apiError && (
          <p className="text-red-500 text-sm">{apiError}</p>
        )}
        
        <div className="grid w-full gap-2">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Connexion en cours..." : "Se connecter"}
          </Button>
          <div className="px-4 py-3">
            <Separator />
          </div>
          <div className="mt-4 text-center text-sm">
            Vous n'avez pas de compte?{" "}
            <Link to={ROUTES.REGISTER} className="underline">
              Enregistrez-vous
            </Link>
          </div>
          <div className="mt-4 text-center text-sm">
            <Link to={ROUTES.RESET_REQUEST} className="underline">
              J'ai oublié mon mot de passe{" "}
            </Link>
          </div>
        </div>
      </form>
    </Form>
  );
}